import * as R from 'ramda'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'
import PropTypes from 'prop-types'

import * as Common from '@rushplay/common'
import * as Forms from '@rushplay/forms'
import * as Herz from '@rushplay/herz'

import * as icons from '../icons'
import { Card } from '../card'
import { Divider } from '../divider'
import { EmailPattern } from '../constants'
import { Heading } from '../heading'
import { InputField } from '../input-field'
import { SubmitButton } from '../submit-button'
import { useServerConfiguration } from '../server-configuration'

const dataSchema = {
  type: 'object',
  properties: {
    email: {
      type: 'string',
      pattern: EmailPattern,
    },
  },
  required: ['email'],
}

const formName = 'password-recovery'
const preloadSchemaKeys = Forms.findTranslationKeys(formName, dataSchema)

export function PasswordRecovery(props) {
  const translate = Herz.I18n.useTranslate()
  const [submitted, setSubmitted] = React.useState(false)
  const { country } = useServerConfiguration()

  function handleSubmit(errors, data) {
    if (R.isEmpty(errors)) {
      setSubmitted(true)
      props.onSubmit(data.email)
    }
  }

  return (
    <Common.Box
      flexBasis="0"
      flexGrow="1"
      flexShrink="1"
      flexDirection="column"
      display="flex"
    >
      <Common.Space padding="2">
        <Heading as="h1" level={2} color="page-nav-heading">
          {translate('password-recovery.title')}
        </Heading>
      </Common.Space>
      <Common.Box
        display="flex"
        backgroundColor="g-bg"
        flexGrow="1"
        flexShrink="1"
        flexBasis="0"
        padding={[null, 4]}
        justifyContent="center"
        alignItems="flex-start"
      >
        <Card
          px={[2, 4]}
          py={4}
          width={['100%', 'auto']}
          maxWidth={[null, '375px']}
        >
          <Common.Box mx="auto" maxWidth="350px">
            {submitted ? (
              <React.Fragment>
                <Common.Box
                  pb={0}
                  color="g-text"
                  fontFamily="head"
                  fontSize={4}
                  fontWeight="bold"
                >
                  {translate('password-recovery.success-page.title')}
                </Common.Box>
                <Common.Text as="p" color="g-text" fontSize={2}>
                  {translate('password-recovery.success-page.content')}
                </Common.Text>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Common.Space pb={2}>
                  <Common.Box
                    pb={0}
                    color="g-text"
                    fontFamily="head"
                    fontSize={4}
                    fontWeight="bold"
                  >
                    {translate('password-recovery.input-page.title')}
                  </Common.Box>
                  <Common.Text as="p" color="g-text" fontSize={2}>
                    {translate('password-recovery.input-page.content')}
                  </Common.Text>
                </Common.Space>
                <Forms.Provider
                  schema={dataSchema}
                  name={formName}
                  onSubmit={handleSubmit}
                >
                  <Common.Flex flexDirection="column">
                    <InputField
                      autoComplete="email"
                      inputMode="email"
                      prependIcon={<icons.Email />}
                      scope="#/properties/email"
                    />
                    <Common.Box pt={4} display="flex" justifyContent="center">
                      <SubmitButton data-testid="password-recovery-submit" />
                    </Common.Box>
                  </Common.Flex>
                </Forms.Provider>
              </React.Fragment>
            )}
            {country.enabled && (
              <React.Fragment>
                <Common.Space pt={4} pb={2}>
                  <Divider />
                </Common.Space>
                <ReactRouter.Link
                  data-testid="password-recovery-register"
                  to="?register=me"
                >
                  <Common.Text
                    fontSize={2}
                    textAlign="center"
                    textDecoration="underline"
                  >
                    {translate('password-recovery.register')}
                  </Common.Text>
                </ReactRouter.Link>
              </React.Fragment>
            )}
          </Common.Box>
        </Card>
      </Common.Box>
    </Common.Box>
  )
}

Herz.I18n.Loader.preload(
  [
    'password-recovery.title',
    'password-recovery.success-page.title',
    'password-recovery.success-page.content',
    'password-recovery.input-page.title',
    'password-recovery.input-page.content',
    'password-recovery.register',
    ...preloadSchemaKeys,
  ],
  PasswordRecovery
)

PasswordRecovery.propTypes = {
  onSubmit: PropTypes.func.isRequired,
}
